import { Module, VuexModule, Mutation, Action } from 'vuex-module-decorators';
import { get } from '@/services/http';

interface DashboardMetrics {
  openPOs: {
    count: number;
    totalValue: number;
    trend: number;
  };
  pendingApprovals: number;
  upcomingDeliveries: {
    count: number;
    suppliers: string[];
  };
  activeAutomations: number;
  nextAutomationRun: string | null;
  statusCounts: {
    draft: number;
    confirmed: number;
    partial_received: number;
    received: number;
    completed: number;
    canceled: number;
  };
  statusTrends: {
    draft: number;
    confirmed: number;
    partial_received: number;
    received: number;
    completed: number;
    canceled: number;
  };
  lowStockAlerts: number;
  payment: {
    awaitingPayment: number;
    overdue: number;
  };
}

interface Activity {
  id: string;
  type: string;
  title: string;
  description: string;
  poId?: string | number;
  poNumber?: string;
  createdAt: string;
  metadata?: any;
}

interface DashboardState {
  metrics: DashboardMetrics | null;
  activities: Activity[];
  loading: boolean;
  error: string | null;
  lastUpdated: Date | null;
  activityPage: number;
  activityHasMore: boolean;
}

@Module({
  namespaced: true,
  name: 'poDashboard'
})
export default class PODashboardModule extends VuexModule implements DashboardState {
  // State
  metrics: DashboardMetrics | null = null;
  activities: Activity[] = [];
  loading = false;
  error: string | null = null;
  lastUpdated: Date | null = null;
  activityPage = 1;
  activityHasMore = true;

  // Getters
  get hasData(): boolean {
    return this.metrics !== null;
  }

  get totalOpenValue(): number {
    return this.metrics?.openPOs?.totalValue || 0;
  }

  get requiresAction(): boolean {
    return (this.metrics?.pendingApprovals || 0) > 0 || 
           (this.metrics?.lowStockAlerts || 0) > 0;
  }

  // Mutations
  @Mutation
  SET_METRICS(metrics: DashboardMetrics) {
    this.metrics = metrics;
  }

  @Mutation
  SET_ACTIVITIES(activities: Activity[]) {
    this.activities = activities;
  }

  @Mutation
  APPEND_ACTIVITIES(activities: Activity[]) {
    this.activities = [...this.activities, ...activities];
  }

  @Mutation
  SET_LOADING(loading: boolean) {
    this.loading = loading;
  }

  @Mutation
  SET_ERROR(error: string | null) {
    this.error = error;
  }

  @Mutation
  SET_LAST_UPDATED(date: Date) {
    this.lastUpdated = date;
  }

  @Mutation
  SET_ACTIVITY_PAGE(page: number) {
    this.activityPage = page;
  }

  @Mutation
  SET_ACTIVITY_HAS_MORE(hasMore: boolean) {
    this.activityHasMore = hasMore;
  }

  @Mutation
  RESET_STATE() {
    this.metrics = null;
    this.activities = [];
    this.error = null;
    this.activityPage = 1;
    this.activityHasMore = true;
  }

  // Actions
  @Action
  async fetchDashboardData() {
    this.context.commit('SET_LOADING', true);
    this.context.commit('SET_ERROR', null);

    try {
      // Fetch metrics and activities separately to isolate issues
      const metricsResponse = await get('/purchase_orders/dashboard/stats');
      
      // Check if response has data property or is direct object
      const metricsData = (metricsResponse as any).data || metricsResponse;
      
      this.context.commit('SET_METRICS', metricsData);
      
      try {
        const activitiesResponse = await get('/purchase_orders/dashboard/activities?page=1&limit=10');
        
        // The response is the direct object, not wrapped in .data
        if (activitiesResponse && (activitiesResponse as any).items) {
          this.context.commit('SET_ACTIVITIES', (activitiesResponse as any).items || []);
          this.context.commit('SET_ACTIVITY_HAS_MORE', (activitiesResponse as any).hasMore || false);
        } else {
          console.error('Activities response is missing items property');
          this.context.commit('SET_ACTIVITIES', []);
          this.context.commit('SET_ACTIVITY_HAS_MORE', false);
        }
      } catch (activitiesError: any) {
        console.error('Activities API failed:', activitiesError);
        this.context.commit('SET_ACTIVITIES', []);
        this.context.commit('SET_ACTIVITY_HAS_MORE', false);
      }
      
      this.context.commit('SET_ACTIVITY_PAGE', 1);
      this.context.commit('SET_LAST_UPDATED', new Date());
    } catch (error: any) {
      console.error('Failed to fetch dashboard data:', error);
      this.context.commit('SET_ERROR', error.message || 'Failed to load dashboard data');
    } finally {
      this.context.commit('SET_LOADING', false);
    }
  }

  @Action
  async loadMoreActivities() {
    if (!this.activityHasMore || this.loading) return;

    try {
      const nextPage = this.activityPage + 1;
      const response = await get(`/purchase_orders/dashboard/activities?page=${nextPage}&limit=10`);

      this.context.commit('APPEND_ACTIVITIES', (response as any).items || []);
      this.context.commit('SET_ACTIVITY_PAGE', nextPage);
      this.context.commit('SET_ACTIVITY_HAS_MORE', (response as any).hasMore || false);
    } catch (error: any) {
      console.error('Failed to load more activities:', error);
      // Don't set global error for pagination failures
    }
  }

  @Action
  async refreshMetrics() {
    try {
      const response = await get('/purchase_orders/dashboard/stats');
      this.context.commit('SET_METRICS', (response as any).data);
      this.context.commit('SET_LAST_UPDATED', new Date());
    } catch (error: any) {
      console.error('Failed to refresh metrics:', error);
      this.context.commit('SET_ERROR', error.message || 'Failed to refresh metrics');
    }
  }

  @Action
  clearDashboard() {
    this.context.commit('RESET_STATE');
  }
}